import { Link } from "react-router-dom";
import { ArrowRight, Clock, EyeOff, SlidersHorizontal, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import { ShieldWm } from "@/components/Watermark";
import { FadeUp, MaskedLine, Eyebrow, DrawLine, ParallaxImg } from "@/components/Reveal";
import QuoteCTA from "@/components/QuoteCTA";

const KHALID_HERO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_elite-transfer-italy/artifacts/n0b7skf4_IMG-20260521-WA0043%282%29.jpg";

const SERVICES = [
  {
    n: "01",
    t: "Trasferimenti aeroportuali",
    d: "Servizio da e verso i principali aeroporti italiani, con particolare attenzione alla puntualità.",
  },
  {
    n: "02",
    t: "Trasferimenti tra città",
    d: "Viaggi personalizzati tra città italiane, anche su lunghe distanze.",
  },
  {
    n: "03",
    t: "Viaggi business",
    d: "Soluzioni dedicate a professionisti, manager e aziende che necessitano di un servizio discreto e affidabile.",
  },
  {
    n: "04",
    t: "Eventi e cerimonie",
    d: "Servizio chauffeur per matrimoni, eventi, ricevimenti e occasioni speciali.",
  },
  {
    n: "05",
    t: "Servizi privati",
    d: "Spostamenti personalizzati per viaggi di lavoro, occasioni private e trasferimenti individuali.",
  },
  {
    n: "06",
    t: "Trasferimenti nazionali e internazionali",
    d: "Trasferimenti su richiesta in tutte le regioni italiane e in Europa.",
  },
  {
    n: "07",
    t: "Servizi personalizzati",
    d: "Possibilità di organizzare servizi su misura in base alle necessità del cliente.",
  },
];

const TRUST = [
  { icon: Clock, t: "Puntuale", d: "Rispetto degli orari concordati." },
  { icon: EyeOff, t: "Discreto", d: "Privacy e professionalità durante ogni trasferimento." },
  { icon: SlidersHorizontal, t: "Personalizzato", d: "Un servizio costruito sulle esigenze del cliente." },
  { icon: MapPin, t: "Nazionale", d: "Trasferimenti in tutta Italia." },
];

export default function Servizi() {
  return (
    <>
      <SEO
        title="Servizi e Preventivo | Zaichauffer – Autista Privato"
        description="Trasferimenti aeroportuali, viaggi business, eventi e cerimonie, trasferimenti tra città e servizi personalizzati con autista privato in tutta Italia e in Europa. Richiedi un preventivo."
      />

      <section className="relative isolate mx-auto max-w-7xl overflow-hidden px-6 pb-20 pt-40 md:pb-28 md:pt-52" data-testid="servizi-hero">
      <ShieldWm className="-z-10 -right-24 top-10 w-[400px] opacity-[0.04]" />
        <div className="grid items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <Eyebrow>Servizi · Preventivo</Eyebrow>
            </FadeUp>
            <h1 className="mt-10 font-serif text-4xl leading-[1.08] tracking-tight text-bone sm:text-5xl lg:text-6xl">
              <MaskedLine>UN VIAGGIO SU MISURA.</MaskedLine>
            </h1>
            <DrawLine className="mt-10" />
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-xl text-[15px] font-light leading-relaxed text-mute">
                Ogni trasferimento è diverso. Inviaci i dettagli del tuo
                viaggio e riceverai un preventivo personalizzato.
              </p>
            </FadeUp>
          </div>
          <div className="hidden lg:col-span-5 lg:block">
            <FadeUp delay={0.25}>
              <ParallaxImg
                src={KHALID_HERO}
                alt="Khalid El Aissaoui, titolare e chauffeur di Zaichauffer, in piedi accanto al veicolo executive"
                className="aspect-[4/5] w-full"
              />
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-36" data-testid="servizi-list">
        <div>
          {SERVICES.map((s, i) => (
            <FadeUp key={s.n} delay={0.04 * i}>
              <div className="group border-t border-white/10 py-10 transition-[padding] duration-500 hover:pl-4 md:py-12">
                <div className="flex flex-col gap-4 md:flex-row md:items-baseline md:gap-14">
                  <span className="font-serif text-sm italic text-gold md:w-10">
                    {s.n}
                  </span>
                  <div className="max-w-2xl flex-1">
                    <h2 className="font-serif text-2xl tracking-wide text-bone transition-colors duration-300 group-hover:text-gold md:text-3xl">
                      {s.t.toUpperCase()}
                    </h2>
                    <p className="mt-4 text-sm font-light leading-relaxed text-mute">
                      {s.d}
                    </p>
                  </div>
                  <Link
                    to="/richiedi-preventivo"
                    aria-label={`Richiedi un preventivo per ${s.t}`}
                    className="mt-2 hidden md:inline-flex md:opacity-0 md:transition-opacity md:duration-300 md:group-hover:opacity-100"
                  >
                    <ArrowRight size={20} className="text-gold" />
                  </Link>
                </div>
              </div>
            </FadeUp>
          ))}
          <div className="border-t border-white/10" />
        </div>
      </section>

      <section className="border-y border-gold/10 bg-coal py-20 md:py-28" data-testid="trust-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST.map((t, i) => (
            <FadeUp key={t.t} delay={0.06 * i}>
              <div className="hairline mb-8 w-12" />
              <t.icon size={24} strokeWidth={1.1} className="text-gold" />
              <h3 className="mt-6 font-serif text-xl tracking-wide text-bone">
                {t.t.toUpperCase()}
              </h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-mute">
                {t.d}
              </p>
            </FadeUp>
          ))}
        </div>
      </section>

      <QuoteCTA />
    </>
  );
}
